import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import {
  Avatar,
  ChatItem,
  ChatsContainer,
  ChatTitle,
  Column,
  Content,
  DollarText,
  Dot,
  EmptyStateContainer,
  FilterButton,
  Header,
  HeaderFilter,
  HeaderSearch,
  InfiniteScrollMarker,
  LeftContent,
  LoaderContainer,
  LoaderMoreContainer,
  MessageText,
  Navigator,
  NewMessageButton,
  NewMessagesContainer,
  NumOfMessages,
  PageContainer,
  RightContent,
  Row,
  SentAtText,
  Title,
  TitleContainer,
} from './MessagesPage.styles';
import { Outlet, useLocation, useNavigate, useOutlet, useParams } from 'react-router-dom';
import { cropText, getInitials } from '../../../utils/util';
import { ReactComponent as PinIcon } from '../../../assets/icons/pin.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/arrow-up.svg';
import { formatChatDate } from '../../../utils/date';
import { CHAT } from '../../../components/App/routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  markChatAsReadLocal,
  selectFetchCollectionsInfo,
  selectFetchInfluencerChatInfo,
  selectFetchInfluencerChatsInfo,
  setNewPurchase,
  setSelectedCollectionId,
} from '../../../store/slices/collections/slice';
import { fetchCollections, fetchInfluencerChats, markChatAsRead } from '../../../store/slices/collections/asyncThunks';
import { Button, ChangeManualChatSpentThreshold, Input, Spinner } from '../../../components';
import { CRM_ORGANIZATION_MEMBER_ROLE, SOCKET_EVENTS, USER_ROLE } from '../../../utils/constants';
import debounce from 'lodash.debounce';
import { Text18Regular } from '../../../components/utils/Text/Text.styles';
import { useSocketEvent } from '../../../hooks/useSocketEvent';
import { selectUser } from '../../../store/slices/user/slice';

const FILTERS = [
  { name: 'All', _id: null },
  { name: 'Unread (Spenders)', _id: 'unreadspenders' },
  { name: 'Follow up', _id: 'followup' },
  { name: 'AI (Spenders)', _id: 'aispenders' },
  { name: 'Non-spenders', _id: 'nonspenders' },
  { name: 'Unread', _id: 'unread' },
];

const MessagesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const user = useSelector(selectUser);
  const loadMoreRef = useRef();
  const listContainerRef = useRef();
  const [showContent, setShowContent] = useState(false);
  const smallScreen = useMediaQuery({ minWidth: 0, maxWidth: 768 });
  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]._id);
  const [page, setPage] = useState(1);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchShown, setSearchShown] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const { data, maxReached, pending, fetchMorePending, refreshing } = useSelector(selectFetchInfluencerChatsInfo);
  const { pending: chatLoading, fetchMorePending: chatFetchMoreLoading } = useSelector(selectFetchInfluencerChatInfo);
  const { data: collectionsData } = useSelector(selectFetchCollectionsInfo);
  const { influencerId } = useParams();
  const stateRef = useRef({
    selectedFilter: FILTERS[0]._id,
    searchValue: '',
    isAtTop: true,
    influencerId,
    refreshing,
  });

  const changeHandler = useCallback(
    text => {
      setSearchValue(text);
      dispatch(
        fetchInfluencerChats({
          page: 1,
          ...(selectedFilter && { collectionId: selectedFilter }),
          ...(text && { search: text }),
          ...(influencerId && { influencerId }),
        }),
      );
    },
    [dispatch, setSearchValue, selectedFilter, influencerId],
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 300), [changeHandler]);

  useEffect(() => {
    dispatch(fetchCollections({ ...(influencerId && { influencerId }) }));
  }, [dispatch, influencerId]);

  useEffect(() => {
    setPage(1);
    dispatch(
      fetchInfluencerChats({
        page: 1,
        ...(influencerId && { influencerId }),
        ...(selectedFilter && { collectionId: selectedFilter }),
      }),
    );
  }, [dispatch, selectedFilter, influencerId]);

  useEffect(() => {
    dispatch(setSelectedCollectionId(selectedFilter));
  }, [selectedFilter, dispatch]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !pending && !fetchMorePending && !maxReached) {
        setPage(prev => prev + 1);
        dispatch(
          fetchInfluencerChats({
            page: page + 1,
            ...(selectedFilter && { collectionId: selectedFilter }),
            ...(searchValue && { search: searchValue }),
            ...(influencerId && { influencerId }),
          }),
        );
      }
    },
    [pending, maxReached, fetchMorePending, page, dispatch, selectedFilter, searchValue, influencerId],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 1.0 });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  useEffect(() => {
    setShowContent(!smallScreen);
  }, [smallScreen]);

  const scrollToTop = useCallback(
    refreshState => {
      const container = listContainerRef.current;
      if (!container) return;
      if (refreshState && !refreshing) {
        setPage(1);
        dispatch(
          fetchInfluencerChats({
            page: 1,
            refresh: true,
            ...(selectedFilter && { collectionId: selectedFilter }),
            ...(searchValue && { search: searchValue }),
            ...(influencerId && { influencerId }),
          }),
        );
      }
      container.scrollTop = 0;
      setIsAtTop(true);
      setNewMessagesCount(0);
    },
    [dispatch, searchValue, selectedFilter, influencerId, refreshing],
  );

  useEffect(() => {
    stateRef.current = {
      selectedFilter,
      searchValue,
      isAtTop,
      influencerId,
      refreshing,
    };
  }, [selectedFilter, searchValue, isAtTop, influencerId, refreshing]);

  const handleRefreshState = useCallback(() => {
    const { selectedFilter, searchValue, isAtTop, influencerId: infId, refreshing: rf } = stateRef.current;

    if (!isAtTop) {
      setNewMessagesCount(prev => prev + 1);
    } else {
      if (!rf) {
        setPage(1);
        dispatch(
          fetchInfluencerChats({
            page: 1,
            refresh: true,
            ...(selectedFilter && { collectionId: selectedFilter }),
            ...(searchValue && { search: searchValue }),
            ...(infId && { influencerId: infId }),
          }),
        );
      }
    }
  }, [dispatch]);

  useSocketEvent(SOCKET_EVENTS.REFRESH_CHAT_LIST, handleRefreshState);

  const handleScroll = () => {
    const container = listContainerRef.current;
    if (!container) return;

    const atTop = container.scrollTop <= 10;
    setIsAtTop(atTop);

    if (atTop) {
      if (newMessagesCount) {
        setNewMessagesCount(0);
        handleRefreshState();
      }
    }
  };

  return (
    <PageContainer>
      {(!smallScreen || (smallScreen && !showContent)) && (
        <Navigator>
          <Header>
            <TitleContainer>
              <Title>Messages</Title>
              <Row>
                {[CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR, CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD].includes(user.role) && (
                  <ChangeManualChatSpentThreshold />
                )}
                {user.role !== CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER && (
                  <NewMessageButton onClick={() => navigate('mass-dm')}>
                    <PlusIcon />
                  </NewMessageButton>
                )}
              </Row>
            </TitleContainer>
            <HeaderSearch>
              {!searchShown && <SearchIcon onClick={() => setSearchShown(true)} height={52} />}
              {searchShown && (
                <Input
                  placeholder="Enter text"
                  hideErrorMessage
                  rightIcon={<CloseIcon color="#1672BA" width={20} height={20} />}
                  onRightIconClick={e => {
                    setSearchShown(false);
                    e.preventDefault();
                    if (searchValue) {
                      debouncedChangeHandler('');
                    }
                  }}
                  onChange={event => {
                    debouncedChangeHandler(event.target.value);
                  }}
                />
              )}
            </HeaderSearch>
            <HeaderFilter>
              {[...FILTERS, ...collectionsData].map(f => {
                return (
                  <FilterButton
                    $selected={f._id === selectedFilter}
                    onClick={() => {
                      dispatch(setSelectedCollectionId(f._id));
                      setSelectedFilter(f._id);
                    }}
                    key={f._id}>
                    {cropText(f.name)}
                  </FilterButton>
                );
              })}
            </HeaderFilter>
          </Header>
          {pending ? (
            <LoaderContainer>
              <Spinner width={120} height={120} />
            </LoaderContainer>
          ) : (
            <ChatsContainer ref={listContainerRef} onScroll={handleScroll}>
              {data.map(chat => {
                const splittedPathname = pathname.split('/');
                const isSelected = splittedPathname[splittedPathname.length - 1] === chat.id;
                const hasUnseenMessages = chat.numOfUnseenMessages > 0;
                return (
                  <ChatItem
                    key={chat.id}
                    $selected={isSelected}
                    onClick={() => {
                      if (!chatLoading && !chatFetchMoreLoading) {
                        setShowContent(true);
                        if (hasUnseenMessages) {
                          dispatch(markChatAsReadLocal(chat.id));
                          dispatch(
                            markChatAsRead({ userInfluencerId: chat.id, ...(influencerId && { influencerId }) }),
                          );
                        }
                        dispatch(setNewPurchase({ chatId: data.userInfluencer?.id, newPurchase: false }));
                        navigate(CHAT.replace(':chatId', chat.id));
                      }
                    }}>
                    <LeftContent>
                      {hasUnseenMessages && <Dot />}
                      <Avatar $newPurchase={chat.newPurchase}>
                        {getInitials(chat.displayName)}
                        {chat.newPurchase && <DollarText>$</DollarText>}
                      </Avatar>
                      <Column>
                        <Row>
                          {chat.isPinned && <PinIcon />}{' '}
                          <ChatTitle $isSeen={!hasUnseenMessages}>{chat.displayName}</ChatTitle>
                        </Row>
                        <MessageText $isSeen={!hasUnseenMessages}>
                          {chat.lastMessage.sender === USER_ROLE.INFLUENCER && 'You: '}
                          {chat.lastMessage.content}
                        </MessageText>
                      </Column>
                    </LeftContent>
                    <RightContent>
                      {chat.lastMessage.timestamp && (
                        <SentAtText $isSeen={!hasUnseenMessages}>
                          {formatChatDate(chat.lastMessage.timestamp)}
                        </SentAtText>
                      )}
                      {hasUnseenMessages && <NumOfMessages>{chat.numOfUnseenMessages}</NumOfMessages>}
                    </RightContent>
                  </ChatItem>
                );
              })}
              {fetchMorePending && (
                <LoaderMoreContainer>
                  <Spinner width={100} height={100} />
                </LoaderMoreContainer>
              )}
              {!!data.length && <InfiniteScrollMarker ref={loadMoreRef} />}
            </ChatsContainer>
          )}
          {!!newMessagesCount && (
            <NewMessagesContainer onClick={() => scrollToTop(true)}>
              <ArrowIcon color="#1672ba" />
            </NewMessagesContainer>
          )}
        </Navigator>
      )}
      {showContent && (
        <Content>
          {outlet ? (
            <Outlet context={[setShowContent, handleRefreshState]} />
          ) : (
            <EmptyStateContainer>
              <Text18Regular>Select any Conversation or send a New Message</Text18Regular>
              {user.role !== CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER && (
                <Button title="New Message" onClick={() => navigate('mass-dm')} />
              )}
            </EmptyStateContainer>
          )}
        </Content>
      )}
    </PageContainer>
  );
};

export default MessagesPage;
